import { DoorProxyForDealsSelection } from 'models/door_proxies_for_deals_selection'

/**
 * Pure logic behind `SelectLocationsPanelFbui` (the fb-ui location picker),
 * extracted so the selection / pagination / label behavior is unit-testable in
 * the node test env (no DOM). The component is a thin presentational shell over
 * these; the Deal-modal migration reuses the same picker, so this is the
 * regression net for both.
 */

/**
 * Chip / row label for a door — the chain name repeats across every door in a
 * chain, so the distinguishing text is the street address (+ city/state), with
 * the chain name as a last-resort fallback for address-less rows.
 */
export const doorLabel = (door: DoorProxyForDealsSelection): string => {
  const locality = [door.city, door.state].filter(Boolean).join(', ')
  return [door.address, locality].filter(Boolean).join(' · ') || door.name
}

/**
 * Split an id list into fixed-size chunks. Used to fan the pre-selected-door
 * label lookup across the endpoint's fixed page size (a single request returns
 * at most `size` rows), so a >`size` edit-flow selection still gets real labels.
 */
export const chunkIds = (ids: number[], size: number): number[][] => {
  if (size <= 0) return ids.length ? [ids] : []
  const chunks: number[][] = []
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size))
  return chunks
}

/** Toggle one door id in/out of the selection (preserves order for adds). */
export const toggleSelection = (selected: number[], id: number): number[] =>
  selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]

/**
 * Add every id on the current page to the selection, skipping ones already
 * selected (no duplicates), preserving existing order then appending the new.
 */
export const addPageToSelection = (
  selected: number[],
  pageIds: number[]
): number[] => {
  const have = new Set(selected)
  return [...selected, ...pageIds.filter((id) => !have.has(id))]
}

/** Remove every id on the current page from the selection. */
export const removePageFromSelection = (
  selected: number[],
  pageIds: number[]
): number[] => {
  const drop = new Set(pageIds)
  return selected.filter((id) => !drop.has(id))
}

/**
 * The page's header-checkbox state: `all` (checked), `some` (indeterminate), or
 * `none` (unchecked). An empty page is never "all".
 */
export const getPageSelectionState = (
  selected: number[],
  pageIds: number[]
): 'none' | 'some' | 'all' => {
  if (pageIds.length === 0) return 'none'
  const have = new Set(selected)
  const hits = pageIds.filter((id) => have.has(id)).length
  if (hits === 0) return 'none'
  return hits === pageIds.length ? 'all' : 'some'
}

/** Total page count for a server-paginated result (never below 1). */
export const computeTotalPages = (count: number, pageSize: number): number =>
  Math.max(1, Math.ceil(count / pageSize))
